import type {
  BankAccount,
  BusinessConfig,
  Company,
  CompanyInfo,
  LenderConfig,
  SolarCompanyInfo,
  UserType,
} from '@kanda-libs/ks-frontend-services';

import type { Address } from 'components/shared/Address/types';

export interface FormattedBank {
  accountName: string;
  accountNumber: string;
  sortCode: string;
}

export const formatBank = (bank: BankAccount): FormattedBank => ({
  accountName: bank?.account_name || 'Not added',
  accountNumber: bank?.account_number || 'Not added',
  sortCode: bank?.sort_code || 'Not added',
});

export interface FormattedInfo {
  avgJobValue: number;
  avgMonthly: number;
  tradeBody: string;
  tradeBodyNumber: string;
  tradeType: string;
  useSubs: string;
  warrantyLength: number;
}

export const formatInfo = (info: CompanyInfo): FormattedInfo => ({
  avgJobValue: info?.average_job_value || 0,
  avgMonthly: info?.average_monthly_jobs || 0,
  tradeBody: info?.trade_body_name || 'Not added',
  tradeBodyNumber: info?.trade_body_number || 'Not added',
  tradeType: info?.trade_type_name || 'Not added',
  useSubs: info?.use_subcontractor || 'Not added',
  warrantyLength: info?.warranty_length || 0,
});

export interface Info extends CompanyInfo {
  referral_code: string;
}

export interface FormattedSolar {
  avgJobValue: number;
  avgMonthly: number;
  tradeType: string;
  solarTrade: string;
  solarTradeNumber: string;
  elecTrade: string;
  elecTradeNumber: string;
  epvs: string;
  useSubs: string;
  warrantyLength: number;
  referral: string;
}

export const formatSolar = (
  info: Info,
  solar: SolarCompanyInfo,
): FormattedSolar => ({
  avgJobValue: info?.average_job_value || 0,
  avgMonthly: info?.average_monthly_jobs || 0,
  tradeType: info?.trade_type_name || 'Not added',
  solarTrade: solar?.solar_trade_association || 'Not added',
  solarTradeNumber: solar?.solar_trade_association_number || 'Not added',
  elecTrade: solar?.electrical_trade_association || 'Not added',
  elecTradeNumber: solar?.electrical_trade_association_number || 'Not added',
  epvs: solar?.epvs_registration_number || 'Not added',
  useSubs: info?.use_subcontractor || 'Not added',
  warrantyLength: info?.warranty_length || 0,
  referral: info?.referral_code || '--',
});

export interface FormattedDirector {
  name: string;
  email: string;
  phone: string;
  status?: string;
  dateOfBirth?: string;
  address: Address;
}

const formatDirectors = (users: UserType[]): null | FormattedDirector[] => {
  if (!users) return null;
  return users.reduce((directors: FormattedDirector[], user) => {
    if (!user?.director_info) return directors;
    directors.push({
      name: `${user?.first_name} ${user?.last_name}`,
      email: user?.email || 'Not added',
      phone: user?.mobile || 'Not added',
      status: user?.director_info?.verification_status,
      dateOfBirth: user?.director_info?.date_of_birth,
      address: {
        buildingName:
          user?.director_info?.home_address?.building_name || undefined,
        buildingNumber:
          user?.director_info?.home_address?.building_number || undefined,
        line1: user?.director_info?.home_address?.line_1 || 'Not added',
        line2: user?.director_info?.home_address?.line_2 || undefined,
        city: user?.director_info?.home_address?.city || 'Not added',
        postcode: user?.director_info?.home_address?.postcode || 'Not added',
      },
    });
    return directors;
  }, [] as FormattedDirector[]);
};

export interface FormattedDetails {
  type: string;
  name: string;
  number: string;
  vat?: string;
  address: Address;
}
const formatDetails = (company?: Company): FormattedDetails => {
  if (company?.company_type === 'limited_company')
    return {
      type: 'Limited company',
      name: company?.limited_company_info?.company_name || 'Not added',
      number: company?.limited_company_info?.company_number || 'Not added',
      vat: company?.limited_company_info?.vat_number || 'Not added',
      address: {
        buildingName:
          company?.limited_company_info?.company_address?.building_name ||
          undefined,
        buildingNumber:
          company?.limited_company_info?.company_address?.building_number ||
          undefined,
        line1:
          company?.limited_company_info?.company_address?.line_1 || 'Not added',
        line2:
          company?.limited_company_info?.company_address?.line_2 || undefined,
        city:
          company?.limited_company_info?.company_address?.city || 'Not added',
        postcode:
          company?.limited_company_info?.company_address?.postcode ||
          'Not added',
      },
    };
  return {
    type: 'Sole trader',
    name: company?.sole_trader_info?.trading_name || 'Not added',
    number: company?.sole_trader_info?.national_insurance_number || 'Not added',
    address: {
      buildingName:
        company?.sole_trader_info?.trading_address?.building_name || undefined,
      buildingNumber:
        company?.sole_trader_info?.trading_address?.building_number ||
        undefined,
      line1: company?.sole_trader_info?.trading_address?.line_1 || 'Not added',
      line2: company?.sole_trader_info?.trading_address?.line_2 || undefined,
      city: company?.sole_trader_info?.trading_address?.city || 'Not added',
      postcode:
        company?.sole_trader_info?.trading_address?.postcode || 'Not added',
    },
  };
};

export interface FormattedContact {
  name: string;
  phone: string;
  email: string;
}

const formatContact = (company?: Company): FormattedContact => ({
  name: company?.contact_info?.contact_name || 'No contact name given',
  phone: company?.contact_info?.contact_phone || 'No contact phone given',
  email: company?.contact_info?.contact_email || 'No contact email given',
});

export interface FormattedCompanyInfo {
  id?: string;
  skipDeposit?: string;
  info: (FormattedInfo | FormattedSolar) & {
    referral: string;
    type?: string;
  };
  details: FormattedDetails;
  bank: FormattedBank;
  directors: null | FormattedDirector[];
  lifecycle?: string;
  contact: FormattedContact;
  fca?: string;
  lenderConfigs?: LenderConfig[];
  businessConfig?: BusinessConfig;
}

export const formatCompanyInfo = (
  company?: Company,
  isSolar = false,
): FormattedCompanyInfo => {
  const bank = formatBank(company?.bank_account as BankAccount);
  const info = isSolar
    ? formatSolar(
        company?.company_info as Info,
        company?.solar_company_info as SolarCompanyInfo,
      )
    : formatInfo(company?.company_info as Info);
  const directors = formatDirectors(company?.users as UserType[]);
  const details = formatDetails(company);
  const contact = formatContact(company);
  return {
    id: company?.id,
    skipDeposit: company?.skip_deposit,
    info: {
      ...info,
      referral: company?.referral_code || '--',
      type: company?.company_type,
    },
    details,
    bank,
    directors,
    lifecycle: company?.lifecycle,
    contact,
    fca: company?.fca_number,
    lenderConfigs: company?.lender_configs,
    businessConfig: company?.business_config,
  };
};
